<template>
  <div class="warning-monitor">
    <!-- 顶部导航栏 -->
    <div class="header">
      <div class="header-content">
        <div class="header-left">
          <i class="el-icon-arrow-left"></i>
          <span class="page-title">{{ pageTitle }}</span>
        </div>

        <!-- 省份筛选下拉 -->
        <el-dropdown trigger="click" @command="handleProvinceChange">
          <span class="province-selector">
            {{ currentProvince }}
            <i class="el-icon-arrow-down"></i>
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="province in provinceList"
              :key="province"
              :command="province"
              :class="{ 'is-active': province === currentProvince }">
              {{ province }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>

      <!-- Tab 切换 -->
      <div class="tab-container">
        <div class="tab-wrapper">
          <button
            :class="['tab-btn', { active: deductModel === '0' }]"
            @click="switchTab('0')">
            电子券库存
          </button>
          <button
            :class="['tab-btn', { active: deductModel === '1' }]"
            @click="switchTab('1')">
            资金池余额
          </button>
        </div>
      </div>
    </div>

    <!-- 状态提示条 -->
    <transition name="slide-down">
      <div v-if="showToast" class="status-toast">
        <div class="toast-content">
          <i class="el-icon-warning"></i>
          <span>{{ toastMessage }}</span>
          <i class="el-icon-close" @click="showToast = false"></i>
        </div>
      </div>
    </transition>

    <!-- 全屏 Loading 遮罩 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <i class="el-icon-loading"></i>
        <p>数据加载中...</p>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 空状态 -->
      <div v-if="!loading && !dataList.length" class="state-container">
        <i class="el-icon-inbox"></i>
        <p>暂无相关数据</p>
      </div>

      <!-- 数据列表 -->
      <div v-else-if="dataList.length" class="data-list">
        <warning-card
          v-for="(item, index) in sortedDataList"
          :key="index"
          :item="item"
          :is-fund-mode="isFundMode" />
      </div>
    </div>

    <!-- 底部用户信息 -->
  </div>
</template>

<script>
import { queryAvailableInfo, PROVINCE_LIST } from '@/api/warning'
import WarningCard from './components/WarningCard.vue'

/**
 * 预警监控主组件
 *
 * 设计模式说明：
 * - 策略模式：通过 deductModel 切换库存/资金两种展示策略
 * - 计算属性实现响应式数据转换，类似 Stream 的 map/sort 操作
 */
export default {
  name: 'WarningMonitor',

  components: {
    WarningCard
  },

  data: function() {
    return {
      // 应用ID（从URL参数获取）
      appid: '',
      // 扣减模式：'0'-库存(电子券), '1'-资金(资金池)
      deductModel: '0',
      // 当前选中省份
      currentProvince: '',
      // 省份列表
      provinceList: [],
      // 原始数据列表
      dataList: [],
      // 加载状态
      loading: false,
      // 提示条显示状态
      showToast: false,
      // 提示消息
      toastMessage: '',
      // 用户信息
      userInfo: {
        name: '测试用户',
        phone: '138****0000'
      }
    }
  },

  computed: {
    /**
     * 页面标题 - 根据模式动态切换
     */
    pageTitle: function() {
      return this.deductModel === '0' ? '电子券库存情况' : '资金池资金情况'
    },

    /**
     * 是否为资金模式
     */
    isFundMode: function() {
      return this.deductModel === '1'
    },

    /**
     * 排序后的数据列表
     * 使用函数式编程思想：链式调用 slice + sort
     * 按可用天数升序排列，预警级别高的排在前面
     */
    sortedDataList: function() {
      var self = this
      return this.dataList.slice().sort(function(a, b) {
        return self.parseDays(a.availableDays) - self.parseDays(b.availableDays)
      })
    }
  },

  created: function() {
    this.initFromUrlParams()
    this.fetchData()
  },

  methods: {
    /**
     * 从 URL 参数初始化数据
     * 支持参数：appid, deductModel, provinceName
     * 兼容两种 URL 格式：
     * - hash 前参数：?provinceName=xxx#/warning
     * - hash 后参数：#/warning?provinceName=xxx
     */
    initFromUrlParams: function() {
      var query = this.$route.query
      var mainQuery = this.getMainUrlParams()

      // 优先使用 hash 前的参数，其次使用 hash 后的参数
      this.appid = mainQuery.appid || query.appid || ''
      this.deductModel = mainQuery.deductModel || query.deductModel || '0'
      this.currentProvince = mainQuery.provinceName || query.provinceName || ''
    },

    /**
     * 获取 # 前面的 URL 参数
     */
    getMainUrlParams: function() {
      var result = {}
      var href = window.location.href
      var hashIndex = href.indexOf('#')
      var urlBeforeHash = hashIndex > -1 ? href.substring(0, hashIndex) : href
      var queryIndex = urlBeforeHash.indexOf('?')

      if (queryIndex > -1) {
        var queryString = urlBeforeHash.substring(queryIndex + 1)
        var pairs = queryString.split('&')
        for (var i = 0; i < pairs.length; i++) {
          var pair = pairs[i].split('=')
          if (pair.length === 2) {
            result[pair[0]] = decodeURIComponent(pair[1])
          }
        }
      }
      return result
    },

    /**
     * 切换 Tab
     * @param {string} model - 模式：'0'-库存, '1'-资金
     */
    switchTab: function(model) {
      if (this.deductModel === model) return
      this.deductModel = model
      this.fetchData()
    },

    /**
     * 省份切换处理
     * @param {string} province - 省份名称
     */
    handleProvinceChange: function(province) {
      this.currentProvince = province
      this.fetchData()
    },

    /**
     * 获取预警数据
     * 请求失败时自动切换至模拟数据
     */
    fetchData: function() {
      var self = this
      this.loading = true
      this.showToast = false

      queryAvailableInfo({
        appid: this.appid,
        deductModel: this.deductModel,
        provinceName: this.currentProvince
      }).then(function(res) {
        self.dataList = res.data || []
      }).catch(function(error) {
        console.error('接口请求失败:', error)
        self.showToast = true
        self.toastMessage = error.message || '网络连接失败'
        self.dataList = []
      }).finally(function() {
        self.loading = false
      })
    },

    /**
     * 生成模拟数据
     * @returns {Array} 模拟预警数据列表
     */
    generateMockData: function() {
      var self = this
      var isFund = this.deductModel === '1'
      var mockList = []
      var count = 5 + Math.floor(Math.random() * 5)

      for (var i = 0; i < count; i++) {
        var stock = Math.floor(Math.random() * 5000) + 100
        var daily = Math.floor(Math.random() * 100) + 10
        var days = Math.floor(stock / daily)

        mockList.push({
          provinceName: self.currentProvince,
          shortName: '演示数据',
          supplierNo: isFund ? 'MOCK-FUND-' + i : 'MOCK-SUP-' + i,
          appId: 'HYXSH',
          supProName: isFund ? null : '100元演示券',
          supProCode: isFund ? null : 'P' + (1000 + i),
          remainingStock: isFund ? null : stock,
          remainingAmount: isFund ? stock * 100 : null,
          dailyConsumption: daily,
          availableDays: i === 0 ? '0.5' : (days > 100 ? '无限' : String(days))
        })
      }
      return mockList
    },

    /**
     * 解析可用天数
     * @param {string} dayStr - 天数字符串
     * @returns {number} 数值化的天数
     */
    parseDays: function(dayStr) {
      if (!dayStr) return 99999
      if (String(dayStr).indexOf('无限') !== -1) return 99999
      return parseFloat(dayStr) || 0
    }
  }
}
</script>

<style scoped>
.warning-monitor {
  min-height: 100vh;
  background-color: #F5F7FA;
  padding-bottom: 60px;
}

/* 顶部导航 */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #fff;
  z-index: 100;
  border-bottom: 1px solid #E4E7ED;
}

.header-content {
  max-width: 768px;
  margin: 0 auto;
  padding: 0 16px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  color: #303133;
}

.header-left i {
  font-size: 20px;
  margin-right: 4px;
}

.page-title {
  font-weight: bold;
  font-size: 18px;
}

/* 省份选择器 */
.province-selector {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #409EFF;
  background: #ECF5FF;
  padding: 6px 12px;
  border-radius: 20px;
  cursor: pointer;
}

.province-selector i {
  margin-left: 4px;
}

/* Tab 切换 */
.tab-container {
  max-width: 768px;
  margin: 0 auto;
  padding: 0 16px 8px;
}

.tab-wrapper {
  display: flex;
  background: #F0F2F5;
  padding: 4px;
  border-radius: 8px;
}

.tab-btn {
  flex: 1;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn.active {
  background: #fff;
  color: #409EFF;
  font-weight: bold;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 状态提示条 */
.status-toast {
  position: fixed;
  top: 112px;
  left: 0;
  right: 0;
  z-index: 99;
  padding: 0 16px;
}

.toast-content {
  max-width: 768px;
  margin: 0 auto;
  background: #FDF6EC;
  border: 1px solid #FAECD8;
  color: #E6A23C;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  display: flex;
  align-items: center;
}

.toast-content i:first-child {
  margin-right: 8px;
}

.toast-content i:last-child {
  margin-left: auto;
  cursor: pointer;
}

/* 主内容区 */
.main-content {
  max-width: 768px;
  margin: 0 auto;
  padding: 120px 16px 20px;
}

.state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 80px;
  color: #909399;
}

.state-container i {
  font-size: 48px;
  margin-bottom: 8px;
  opacity: 0.5;
}

.state-container p {
  font-size: 14px;
}

.data-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 全屏 Loading 遮罩 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  text-align: center;
  color: #409EFF;
}

.loading-spinner i {
  font-size: 40px;
  animation: spin 1s linear infinite;
}

.loading-spinner p {
  margin-top: 12px;
  font-size: 14px;
  color: #606266;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 底部用户信息 */
.user-info-bar {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(48, 49, 51, 0.8);
  color: #fff;
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
}

.user-info-bar i {
  margin-right: 4px;
}

/* 过渡动画 */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

/* 下拉菜单激活项 */
.el-dropdown-menu__item.is-active {
  color: #409EFF;
  font-weight: bold;
}
</style>
